import { z } from 'zod';
import { PlatformIdSchema } from './platform';

/**
 * Platform OAuth connection wire shape. Phase-1 only Wix is implementable
 * (ADR 0004); the schema is platform-agnostic so Etsy / eBay / Shopify
 * follow the same pattern with per-platform OAuth flows behind the same
 * record shape.
 *
 * Tokens never leave the backend — `access_token` / `refresh_token` live
 * in Firestore and are read by the refresh middleware only. The frontend
 * receives only descriptive metadata via these schemas.
 */

export const CONNECTION_STATUSES = ['active', 'expired', 'revoked'] as const;
export const ConnectionStatusSchema = z.enum(CONNECTION_STATUSES);
export type ConnectionStatus = z.infer<typeof ConnectionStatusSchema>;

/**
 * One bound platform install for a workspace. `externalAccountId` is the
 * platform-side install id (Wix calls this `instance_id`; Etsy uses
 * `shop_id`; eBay uses `user_id`). The schema is identical across
 * platforms.
 *
 * Field choices:
 * - `id` — our internal id, used as the Firestore doc key.
 * - `scopes` — what we asked for at consent time; lets the UI surface
 *   "this connection can publish" vs "read-only".
 * - `expiresAt` — null for connections that never expire (Etsy oauth-2
 *   long-lived tokens), ISO 8601 timestamp otherwise.
 * - `lastUsedAt` — null until first API call; helps the user see which
 *   connections are stale.
 */
export const ConnectionRecordSchema = z.object({
  id: z.string().min(1),
  workspaceId: z.string().min(1),
  platformId: PlatformIdSchema,
  externalAccountId: z.string().min(1),
  externalAccountLabel: z.string(),
  status: ConnectionStatusSchema,
  scopes: z.array(z.string()),
  connectedAt: z.string().datetime(),
  lastUsedAt: z.string().datetime().nullable(),
  expiresAt: z.string().datetime().nullable(),
});
export type ConnectionRecord = z.infer<typeof ConnectionRecordSchema>;

/**
 * `GET /api/connections` — lists all bound platforms for the active
 * workspace. Empty array is the common case (no platforms connected
 * yet). The Connections page reads this to flip each platform card
 * between "Connect" and "Connected" affordances.
 */
export const ListConnectionsResponseSchema = z.object({
  items: z.array(ConnectionRecordSchema),
});
export type ListConnectionsResponse = z.infer<typeof ListConnectionsResponseSchema>;

/**
 * OAuth-initiation response — the URL the user is bounced to on the
 * platform side. Lands in iter 41 (Wix install + callback routes); the
 * shape is defined here so iter 40 can stay frozen once shipped.
 */
export const ConnectInitiationSchema = z.object({
  redirectUrl: z.string().url(),
  state: z.string().min(1),
});
export type ConnectInitiation = z.infer<typeof ConnectInitiationSchema>;
